import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Button, IconButton, CircularProgress } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
// import EditIcon from '@mui/icons-material/Edit';
// import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { catalogueService } from '../../services/catalogue.service';
import { toast } from 'react-toastify';
import { useMessageHandler } from '../../hooks/useMessageHandler';
import { useCategoryLabelMaps } from '../../hooks/useCategoryLabelMaps';
import { formatCategoryLabel, formatCategoryLabels } from '../../utils/categoryLabels';

const ProfileDetail: React.FC = () => {
  const handleMessage = useMessageHandler();
  const { id } = useParams();
  const navigate = useNavigate();
  const { maps: categoryLabels } = useCategoryLabelMaps();
  const [showFullPolicy, setShowFullPolicy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<any>(null);
  const [versionId, setVersionId] = useState<number>(1);
  const [approving, setApproving] = useState(false);
  const [suspending, setSuspending] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        const responseData = await catalogueService.getProfileById(id);
        const profileData = responseData.data?.vendorProfile || null;
        setProfile(profileData);
        setVersionId(responseData.data?.vendorProfile?.versionId || 1);
      } catch (error) {
        console.error('Error fetching profile:', error);
        toast.error('Failed to load profile details');
        setProfile(null);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [id]);

  const navigateToProfiles = useCallback(() => {
    navigate('/catalogue?tab=profiles');
  }, [navigate])

  const handleApprove = async () => {
    if (!profile || !id) return;
    
    setApproving(true);

    const response = await catalogueService.approveProfile(id, versionId);

    if (response.ok) {
      if (response.messageInfo) {
        handleMessage(response.messageInfo)
      }
    } else {
      handleMessage({
        error: response.error
      })
    }

    setApproving(false);

    navigateToProfiles();
  };

  const handleSuspend = async () => {
    if (!profile || !id) return;

    setSuspending(true);

    const response = await catalogueService.suspendProfile(id, versionId);

    if (response.ok) {
      if (response.messageInfo) {
        handleMessage(response.messageInfo)
      }
    } else {
      handleMessage({
        error: response.error
      })
    }

    setSuspending(false);

    navigateToProfiles();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!profile) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Typography>Profile not found</Typography>
      </Box>
    );
  }

  const PropertyRow = ({ label, value, noBorder }: { label: string; value: string; noBorder?: boolean }) => (
    <Box sx={{ display: 'flex', gap: 'var(--palette-light-1-value)', padding: 'var(--palette-light-1-value) 0', borderBottom: noBorder ? 'none' : '1px solid #F1F4F4' }}>
      <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-primary-value-hex)', flex: 1 }}>{label}</Typography>
      <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-primary-value-hex)', flex: 1 }}>{value}</Typography>
    </Box>
  );

  const disableActions = approving || suspending;

  return (
    <Box
      sx={{
        padding: {
          xs: '24px 16px',
          sm: '40px 40px',
          md: '40px 80px',
        },
        gap: 'var(--palette-light-6-value)',
        width: '100%',
        minHeight: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto'
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 'var(--palette-light-4-value)' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--palette-light-2-value)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 'var(--palette-light-3-value)' }}>
            <IconButton
              onClick={navigateToProfiles}
              sx={{
                width: 'var(--palette-light-8-value)',
                height: 'var(--palette-light-8-value)',
                border: '1px solid var(--palette-light-ios-separator-value-hex)',
                borderRadius: 'var(--palette-light-4-value)',
                color: 'var(--palette-light-brand-primary-value-hex)'
              }}
            >
              <ChevronLeftIcon />
            </IconButton>
            <Box>
              <Typography sx={{ fontSize: 'var(--palette-light-font-size-h1-value)', fontWeight: 700, lineHeight: 1.15, color: 'var(--palette-light-text-primary-value-hex)' }}>
                {profile.companyName || profile.contactName || 'Vendor'}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 'var(--palette-light-2-value)' }}>
          <Button
            variant="contained"
            loading={approving}
            disabled={disableActions}
            onClick={handleApprove}
            sx={{
              backgroundColor: 'var(--palette-light-brand-primary-value-hex)',
              color: 'var(--palette-light-text-foreground-value-hex)',
              textTransform: 'none',
              fontSize: 'var(--palette-light-font-size-default-value)',
              fontWeight: 700,
              borderRadius: 'var(--palette-light-4-value)',
              padding: '2px 32px 0',
              height: 'var(--palette-light-14-value)',
              '&:hover': {
                backgroundColor: 'var(--palette-light-brand-primary-value-hex)',
                opacity: 0.9
              }
            }}
          >
            Approve
          </Button>
          <Button
            variant="outlined"
            loading={suspending}
            disabled={disableActions}
            onClick={handleSuspend}
            sx={{
              borderColor: 'var(--palette-light-brand-primary-value-hex)',
              color: 'var(--palette-light-brand-primary-value-hex)',
              textTransform: 'none',
              fontSize: 'var(--palette-light-font-size-default-value)',
              fontWeight: 700,
              borderRadius: 'var(--palette-light-4-value)',
              padding: '2px 32px 0',
              height: 'var(--palette-light-14-value)',
              '&:hover': {
                borderColor: 'var(--palette-light-brand-primary-value-hex)',
                backgroundColor: 'rgba(103, 49, 79, 0.04)'
              }
            }}
          >
            Suspend
          </Button>
          {/* <IconButton
            sx={{
              width: 'var(--palette-light-14-value)',
              height: 'var(--palette-light-14-value)',
              border: '1px solid var(--palette-light-brand-primary-value-hex)',
              borderRadius: 'var(--palette-light-4-value)',
              color: 'var(--palette-light-brand-primary-value-hex)'
            }}
          >
            <MoreHorizIcon />
          </IconButton> */}
        </Box>
      </Box>

      {/* Content */}
      <Box
        sx={{
          display: 'flex',
          gap: 'var(--palette-light-10-value)'
        }}
      >
        {/* Left column */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--palette-light-8-value)', minWidth: '332px', maxWidth: '332px' }}>
          {/* Image upload */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--palette-light-6-value)' }}>
            <Box sx={{ position: 'relative', width: '100%', height: '252.55px', borderRadius: 'var(--palette-light-3-value)', overflow: 'hidden', border: '1px solid #C5CBD3' }}>
            <Box
              component={profile.coverImageURL ? 'img' : 'div'}
              src={profile.coverImageURL || undefined}
              alt={`${profile.companyName || profile.contactName} cover`}
              sx={{
                width: '100%',
                height: '100%',
                backgroundColor: 'var(--palette-light-brand-tertiary-value-hex)',
                objectFit: 'cover'
              }}
            />
              {/* <IconButton
                sx={{
                  position: 'absolute',
                  top: 'var(--palette-light-2-value)',
                  right: 'var(--palette-light-2-value)',
                  width: 'var(--palette-light-8-value)',
                  height: 'var(--palette-light-8-value)',
                  backgroundColor: 'var(--palette-light-text-foreground-value-hex)',
                  borderRadius: 'var(--palette-light-4-value)',
                  '&:hover': {
                    backgroundColor: 'var(--palette-light-text-foreground-value-hex)',
                    opacity: 0.9
                  }
                }}
              >
                <EditIcon sx={{ fontSize: '16px', color: 'var(--palette-light-brand-primary-value-hex)' }} />
              </IconButton> */}
            </Box>
          </Box>

          {/* Primary info */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--palette-light-4-value)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography sx={{ fontSize: 'var(--palette-light-font-size-h2-value)', fontWeight: 700, lineHeight: 1.15, color: 'var(--palette-light-text-primary-value-hex)' }}>
                {profile.companyName || profile.contactName || 'Vendor'}
              </Typography>
              {/* <IconButton
                sx={{
                  width: 'var(--palette-light-8-value)',
                  height: 'var(--palette-light-8-value)',
                  backgroundColor: 'var(--palette-light-brand-primary-value-hex)',
                  borderRadius: 'var(--palette-light-4-value)',
                  '&:hover': {
                    backgroundColor: 'var(--palette-light-brand-primary-value-hex)',
                    opacity: 0.9
                  }
                }}
              >
                <EditIcon sx={{ fontSize: '16px', color: 'var(--palette-light-text-foreground-value-hex)' }} />
              </IconButton> */}
            </Box>
            <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-primary-value-hex)' }}>
              {profile.description || 'No description available'}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--palette-light-1-value)', padding: 'var(--palette-light-1-value) 0', borderTop: '1px solid #F1F4F4', borderBottom: '1px solid #F1F4F4' }}>
              <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-primary-value-hex)' }}>Contact Info:</Typography>
              <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-primary-value-hex)' }}>
                Phone: {profile.phone || 'N/A'}
              </Typography>
              <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-primary-value-hex)' }}>
                Email address: {profile.email || 'N/A'}
              </Typography>
              {profile.address && (
                <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-primary-value-hex)' }}>
                  Visit {profile.companyName || profile.contactName} at {profile.address}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Category and tags */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--palette-light-4-value)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography sx={{ fontSize: '18px', fontWeight: 700, lineHeight: 1.25, color: 'var(--palette-light-text-primary-value-hex)' }}>
                Service Category and Tags
              </Typography>
              {/* <IconButton
                sx={{
                  width: 'var(--palette-light-8-value)',
                  height: 'var(--palette-light-8-value)',
                  backgroundColor: 'var(--palette-light-brand-primary-value-hex)',
                  borderRadius: 'var(--palette-light-4-value)',
                  '&:hover': {
                    backgroundColor: 'var(--palette-light-brand-primary-value-hex)',
                    opacity: 0.9
                  }
                }}
              >
                <EditIcon sx={{ fontSize: '16px', color: 'var(--palette-light-text-foreground-value-hex)' }} />
              </IconButton> */}
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <PropertyRow
                label="Category:"
                value={
                  formatCategoryLabel(
                    profile.categoryTree?.mainCategory,
                    categoryLabels?.mainById || {},
                  ) ||
                  profile.categoryTreeEnriched?.mainCategoryName ||
                  'N/A'
                }
              />
              <PropertyRow
                label="Sub categories:"
                value={
                  formatCategoryLabels(
                    profile.categoryTree?.subCategories,
                    categoryLabels?.subByKey || {},
                  ) ||
                  profile.categoryTreeEnriched?.subCategoryNames?.join(', ') ||
                  'N/A'
                }
              />
              <PropertyRow
                label="Types:"
                value={
                  formatCategoryLabels(
                    profile.categoryTree?.types,
                    categoryLabels?.typeByKey || {},
                  ) ||
                  profile.categoryTreeEnriched?.typeNames?.join(', ') ||
                  'N/A'
                }
              />
              <PropertyRow
                label="Tags:"
                value={
                  profile.categoryTree?.items?.join(', ') ||
                  profile.categoryTreeEnriched?.itemNames?.join(', ') ||
                  'N/A'
                }
              />
              <PropertyRow label="Experiences:" value={profile.occasions?.map((o: any) => (typeof o === 'string' ? o : o?.title)).filter(Boolean).join(', ') || 'N/A'} noBorder />
            </Box>
          </Box>

          {/* Locations served */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--palette-light-4-value)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography sx={{ fontSize: '18px', fontWeight: 700, lineHeight: 1.25, color: 'var(--palette-light-text-primary-value-hex)' }}>
                Locations served
              </Typography>
              {/* <IconButton
                sx={{
                  width: 'var(--palette-light-8-value)',
                  height: 'var(--palette-light-8-value)',
                  backgroundColor: 'var(--palette-light-brand-primary-value-hex)',
                  borderRadius: 'var(--palette-light-4-value)',
                  '&:hover': {
                    backgroundColor: 'var(--palette-light-brand-primary-value-hex)',
                    opacity: 0.9
                  }
                }}
              >
                <EditIcon sx={{ fontSize: '16px', color: 'var(--palette-light-text-foreground-value-hex)' }} />
              </IconButton> */}
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ padding: 'var(--palette-light-1-value) 0', borderBottom: '1px solid #F1F4F4' }}>
                <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-primary-value-hex)' }}>
                  {profile.city || profile.address || 'N/A'}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Right column */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--palette-light-8-value)', flex: 1 }}>
          {/* Legal Information */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--palette-light-4-value)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography sx={{ fontSize: '18px', fontWeight: 700, lineHeight: 1.25, color: 'var(--palette-light-text-primary-value-hex)' }}>
                Legal Information
              </Typography>
              {/* <IconButton
                sx={{
                  width: 'var(--palette-light-8-value)',
                  height: 'var(--palette-light-8-value)',
                  backgroundColor: 'var(--palette-light-brand-primary-value-hex)',
                  borderRadius: 'var(--palette-light-4-value)',
                  '&:hover': {
                    backgroundColor: 'var(--palette-light-brand-primary-value-hex)',
                    opacity: 0.9
                  }
                }}
              >
                <EditIcon sx={{ fontSize: '16px', color: 'var(--palette-light-text-foreground-value-hex)' }} />
              </IconButton> */}
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <PropertyRow label="Registered name:" value={profile.registeredName || 'N/A'} />
              <PropertyRow label="Trade License Number:" value={profile.tradeLicenseNumber || 'N/A'} />
              <PropertyRow label="Commercial Registration No.:" value={profile.commercialRegistrationNo || 'N/A'} />
              <PropertyRow label="Registered Address:" value={profile.registeredAddress || 'N/A'} noBorder />
            </Box>
          </Box>

          {/* Service Policy */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--palette-light-4-value)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography sx={{ fontSize: '18px', fontWeight: 700, lineHeight: 1.25, color: 'var(--palette-light-text-primary-value-hex)' }}>
                Service Policy
              </Typography>
              {/* <IconButton
                sx={{
                  width: 'var(--palette-light-8-value)',
                  height: 'var(--palette-light-8-value)',
                  backgroundColor: 'var(--palette-light-brand-primary-value-hex)',
                  borderRadius: 'var(--palette-light-4-value)',
                  '&:hover': {
                    backgroundColor: 'var(--palette-light-brand-primary-value-hex)',
                    opacity: 0.9
                  }
                }}
              >
                <EditIcon sx={{ fontSize: '16px', color: 'var(--palette-light-text-foreground-value-hex)' }} />
              </IconButton> */}
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--palette-light-8-value)' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', gap: 'var(--palette-light-1-value)', padding: 'var(--palette-light-1-value) 0', borderBottom: '1px solid #F1F4F4' }}>
                  <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-primary-value-hex)', flex: 1 }}>Orders can be placed accepted not later than</Typography>
                  <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-primary-value-hex)' }}>
                    {profile.acceptOrdersDeadline || 'N/A'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 'var(--palette-light-1-value)', padding: 'var(--palette-light-1-value) 0', borderBottom: '1px solid #F1F4F4' }}>
                  <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-primary-value-hex)', flex: 1 }}>Changes accepted not later than</Typography>
                  <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-primary-value-hex)' }}>
                    {profile.acceptChangesDeadline || 'N/A'}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ position: 'relative' }}>
                <Typography 
                  sx={{ 
                    fontSize: 'var(--palette-light-font-size-default-value)', 
                    color: 'var(--palette-light-text-primary-value-hex)', 
                    whiteSpace: 'pre-line',
                    maxHeight: showFullPolicy ? 'none' : '220px',
                    overflow: 'hidden'
                  }}
                >
                  {profile.servicePolicy || 'No service policy available'}
                </Typography>
                {!showFullPolicy && profile.servicePolicy && profile.servicePolicy.length > 500 && (
                  <Box sx={{ 
                    position: 'absolute', 
                    bottom: 0, 
                    left: 0, 
                    right: 0, 
                    height: '80px', 
                    background: 'linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 1) 100%)',
                    display: 'flex',
                    alignItems: 'flex-end',
                    justifyContent: 'flex-end'
                  }}>
                    <Button 
                      onClick={() => setShowFullPolicy(true)}
                      sx={{ 
                        fontSize: 'var(--palette-light-font-size-small-value)', 
                        color: 'var(--palette-light-brand-primary-value-hex)',
                        textTransform: 'none',
                        minWidth: 'auto',
                        padding: 0
                      }}
                    >
                      more
                    </Button>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ProfileDetail;


